import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { BrandContext } from "../../App";
import EngagementChart from "../simple/EngagementChart";
import EngagementFilter from "../simple/EngagementFilter";
import EngagementAchievementBox from "../simple/EngagementAchievementBox";
import { NightlightOutlined, StackedLineChart } from "@mui/icons-material";

const TeamEngagementPanel = () => {
  const { brand, user, authToken } = React.useContext(BrandContext);
  const theme = useTheme();
  const teamId = user?.team?.id;
  const minCustomInput = 1;
  const maxCustomInput = 120;

  const [dataPoints, setDataPoints] = useState([]);
  const [filter, setFilter] = useState("all-members");
  const [teamUsers, setTeamUsers] = useState([]);
  const [isManager, setIsManager] = useState(false);
  const [days, setDays] = useState(7);
  const [mostActive, setMostActive] = useState(null);
  const [leastActive, setLeastActive] = useState(null);
  const [customInput, setCustomInput] = useState("");
  const [customInputError, setCustomInputError] = useState(false);

  const handleChange = (event) => {
    setFilter(event.target.value);
  };

  const handleViewChange = (_, newValue) => {
    if (newValue !== null) {
      setDays(newValue);
    }
  };

  const cache = useRef({});

  useEffect(() => {
    if (customInput.length === 0) return;
    const timeOutId = setTimeout(() => setDays(customInput), 500);
    return () => clearTimeout(timeOutId);
  }, [customInput]);

  useEffect(() => {
    if (!teamId) return;

    const cacheKey = `${teamId}_${filter}_${days}`;
    if (cache.current[cacheKey]) {
      setDataPoints(cache.current[cacheKey].points);
      setMostActive(cache.current[cacheKey].most);
      setLeastActive(cache.current[cacheKey].least);
      return;
    }

    const url = new URL(`${brand.api_base_url}/leaderboard_data`);
    url.searchParams.set("days", days);

    if (filter === "all-members") {
      url.searchParams.set("team_id", teamId);
    } else {
      url.searchParams.set("team_id", teamId);
      url.searchParams.set("user_id", filter);
    }

    fetch(url, {
      headers: { Authorization: `Token ${authToken}` },
    })
      .then((res) => res.json())
      .then((data) => {
        const sorted = [...data.data].sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        );
        const points = sorted.map((entry) => ({
          userId: entry.user_id,
          date: entry.date,
          numberOfSims: entry.number_of_sims,
          averageScore: entry.average_total_score,
        }));

        let highest = points[0];
        let lowest = points[0];

        for (const point of points) {
          if (point.numberOfSims > highest.numberOfSims) highest = point;
          if (point.numberOfSims < lowest.numberOfSims) lowest = point;
        }

        cache.current[cacheKey] = {
          points,
          most: highest,
          least: lowest,
        };

        setDataPoints(points);
        setMostActive(highest);
        setLeastActive(lowest);
      })
      .catch((err) => console.error(err.message));
  }, [teamId, filter, days]);

  useEffect(() => {
    if (!teamId || dataPoints.length === 0 || dataPoints[0].userId === "-")
      return;

    fetch(`${brand.api_base_url}/team_users?team_id=${teamId}`, {
      headers: { Authorization: `Token ${authToken}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch team users");
        return res.json();
      })
      .then((data) => {
        setTeamUsers(data);
        setIsManager(true);
      })
      .catch((err) => console.error(err));
  }, [teamId, dataPoints]);

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        padding: theme.containers.paddingMobile,
        flexGrow: 1,
        color: theme.palette.text.primary,
        backgroundColor: "transparent",
        border: `2px solid`,
        borderColor: theme.palette.border.tertiary,
        borderRadius: theme.shape.radius,
        boxShadow:
          "0px 12px 16px -4px rgba(0, 91, 140, 0.10), 0px 4px 6px -2px rgba(0, 91, 140, 0.03), 0px 2px 2px -1px rgba(0, 91, 140, 0.04)",
      }}
    >
      <Box>
        <Typography
          sx={{
            fontSize: theme.typography.textMD.fontSize,
            lineHeight: theme.typography.textMD.lineHeight,
            fontWeight: theme.typography.fontWeightMedium,
            marginBottom: "12px",
          }}
        >
          Team Engagement
        </Typography>
        <Typography
          sx={{
            fontSize: "14px",
            lineHeight: "1.3",
            fontWeight: theme.typography.fontWeightLight,
            fontStyle: "italic",
            mb: theme.customSpacing["2xl"],
          }}
        >
          See how actively your team is engaging with the program. Switch
          between day, week, or month views to spot patterns!
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          mb: theme.customSpacing.xl,
          [theme.breakpoints.down("md")]: {
            flexDirection: "column",
            gap: "10px",
          },
        }}
      >
        <Box
          sx={{
            mr: theme.customSpacing["8xl"],
            [theme.breakpoints.down("md")]: {
              mr: 0,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
            }}
          >
            <Typography
              sx={{
                fontSize: theme.typography.textXS.fontSize,
                lineHeight: theme.typography.textXS.lineHeight,
                fontWeight: theme.typography.fontWeightLight,
                fontStyle: "italic",
                mb: theme.customSpacing.sm,
              }}
            >
              View:
            </Typography>
            <Box sx={{ flexGrow: 1 }}></Box>
            <Typography
              sx={{
                fontSize: theme.typography.textXS.fontSize,
                lineHeight: theme.typography.textXS.lineHeight,
                fontWeight: theme.typography.fontWeightLight,
                fontStyle: "italic",
                textAlign: "left",
                width: 100,
                mb: theme.customSpacing.sm,
                marginRight: theme.customSpacing.lg,
              }}
            >
              Custom range:
            </Typography>
          </Box>

          <ToggleButtonGroup
            value={days}
            exclusive
            onChange={handleViewChange}
            sx={{
              backgroundColor: "transparent",
              height: 43,
              [theme.breakpoints.down("md")]: {
                justifyContent: "space-between",
                width: "100%",
              },
              "& .MuiToggleButton-root": {
                color: theme.palette.foreground.button_secondary,
                textTransform: "none",
                border: "none",
                borderRadius: 2,
                px: theme.customSpacing.lg,
                py: theme.customSpacing.md,
                fontWeight: theme.typography.fontWeightMedium,
                "&.Mui-selected": {
                  backgroundColor: "transparent",
                  color: theme.palette.foreground.badge_icon,
                  border: "1px solid",
                  borderColor: theme.palette.border.tertiary,
                },
                "&:hover": {
                  backgroundColor: "rgba(154, 92, 255, 0.05)",
                },
              },
            }}
          >
            <div>
              <ToggleButton value={7}>Weekly</ToggleButton>
              <ToggleButton value={31}>Monthly</ToggleButton>
            </div>
            <TextField
              type="number"
              placeholder={`${minCustomInput}-${maxCustomInput} days`}
              variant="outlined"
              size="medium"
              error={customInputError}
              helperText={
                customInputError
                  ? `${minCustomInput}-${maxCustomInput} days`
                  : ""
              }
              FormHelperTextProps={{
                sx: {
                  fontSize: theme.typography.textXS.fontSize,
                  color: theme.palette.text.error_primary,
                  overflow: "",
                },
              }}
              onInput={(e) => {
                const val = Number(e.target.value);
                if (val >= minCustomInput && val <= maxCustomInput) {
                  setCustomInput(val);
                  setCustomInputError(false);
                } else {
                  setCustomInputError(true);
                }
              }}
              inputProps={{
                min: minCustomInput,
                max: maxCustomInput,
                style: {
                  textAlign: "center",
                },
              }}
              sx={{
                mx: theme.customSpacing.lg,
                my: theme.customSpacing.md,
                width: 100,
                input: {
                  color: theme.palette.foreground.button_secondary,
                  fontSize: theme.typography.textXS.fontSize,
                  padding: theme.customSpacing.xs,
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.border.tertiary,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.border.tertiary,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.border.brand,
                },
              }}
            />
          </ToggleButtonGroup>
        </Box>
        <EngagementFilter
          isManager={isManager}
          teamUsers={teamUsers}
          filter={filter}
          handleChange={handleChange}
        />
      </Box>
      <EngagementChart dataPoints={dataPoints} />
      <Box
        sx={{
          display: "flex",
          gap: theme.customSpacing["6xl"],
          [theme.breakpoints.down("md")]: {
            flexDirection: "column",
            gap: "20px",
          },
        }}
      >
        {mostActive && (
          <EngagementAchievementBox
            title="Most Active Day"
            icon={<StackedLineChart sx={{ fontSize: "14px" }} />}
            date={mostActive.date}
            count={mostActive.numberOfSims}
          />
        )}

        {leastActive && (
          <EngagementAchievementBox
            title="Least Active Day"
            icon={<NightlightOutlined sx={{ fontSize: "14px" }} />}
            date={leastActive.date}
            count={leastActive.numberOfSims}
          />
        )}
      </Box>
    </Card>
  );
};

export default TeamEngagementPanel;
